import React, { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TableCell } from "@mui/material";
import { useDispatch } from "react-redux";
import { IconButton } from "@mui/material";
import Info from "@mui/icons-material/Info";

// COMPONENTS
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";
import StatusCartComponent from "@/main/components/shared/status/statusCartComponent";
import PendingDeadlineCell from "../pendingDeadlineCell/pendingDeadlineCell";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import TooltipComponent from "@/main/components/shared/tooltip/tooltipComponent";
import { KOGTableRow } from "@/main/components/shared/boxGrid/styles";

// SERVICES
import { quoteApi } from "@/main/api";
import { QuoteCartsAnswerPendingRequest } from "@/main/models/request/quoteRequest";
import { PaginationEnum } from "@/main/services/enum/PaginationEnum";
import { QuoteCartsAnswerPendingResponse } from "@/main/models/response";
import { getCartCountStatus } from "@/main/store/cartCountStatus";
import { StatusCartEnum } from "@/main/services/enum/StatusCartEnum";
import {
	getDefaultMessageResultsFound,
	getGuidScreenByRoute,
	getTranslateByKey,
	isShowControlByElementId,
} from "@/main/services/utils";
import { userIsSupplier } from "@/main/services/utilsUser";
import { RoutesConstants } from "@/main/routes/routesConstants";
import { ISearchCartForm } from "../../searchPendingToAnswer";

import * as S from "./styles";
import { Controls } from "@/main/services/constants/constantsControl";
import ClaimDetailsSearchPendingAnswer from "../ClaimDetailsSearchPendingAnswer/ClaimDetailsSearchPendingAnswer";
import { TextEllipsis } from "@/main/components/shared/styles";

export const INITIAL_GRID_STATE: IGridData = {
	totalItems: 0,
	isLoading: false,
	data: [],
};
export interface IGridData {
	data: QuoteCartsAnswerPendingResponse[];
	totalItems: number;
	isLoading: boolean;
}

interface IUserSearchGridProps {
	gridData: IGridData;
	formFilters: QuoteCartsAnswerPendingRequest | null;
	setGridData: React.Dispatch<React.SetStateAction<IGridData>>;
	parameterValueDateRange: number;
	initalDateForm: (range?: number) => void;
	INITIAL_FORM_STATE: ISearchCartForm;
}

function PendingSearchGrid({
	gridData,
	formFilters,
	setGridData,
	parameterValueDateRange,
	initalDateForm,
	INITIAL_FORM_STATE,
}: IUserSearchGridProps) {
	// Hooks
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const scrollTargetId = `pending-search-grid-${useId()}`;

	// States
	const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState(false);
	const [hasShowMore, setHasShowMore] = useState(false);
	const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
	const [openModalClaimDetails, setOpenModalClaimDetails] = useState<boolean>(false);
	const [selectedGuidQuote, setSelectedGuidQuote] = useState<string | null>(null);

	// ASYNC FUNCTIONS
	function onLoadInfiniteScroll(request: QuoteCartsAnswerPendingRequest, refreshGrid: boolean) {
		setIsLoadingInfiniteScroll(true);

		quoteApi
			.getCartsPendingToAnswer(request)
			.then((response) => {
				let resultData: QuoteCartsAnswerPendingResponse[] = [];
				let resultTotalItems: number = 0;
				if (response.data && response.data.results && response.data.results.length > 0) {
					resultData = response.data.results;
					resultTotalItems = response.data.totalNumberOfRecords;
				}

				if (gridData.data.length > 0 && !refreshGrid) {
					resultData = [...gridData.data, ...resultData];
				} else if (response.data.results.length === 0 && refreshGrid) {
					resultData = [];
				}

				setGridData({
					isLoading: false,
					totalItems: resultTotalItems,
					data: resultData,
				});

				// Disable show more
				if (resultTotalItems < PaginationEnum.PageSize10 || resultTotalItems === resultData.length) {
					setHasShowMore(false);
				}
			})
			.finally(() => setIsLoadingInfiniteScroll(false));
	}

	function onLoadSearchGrid(refreshGrid: boolean) {
		const index = refreshGrid ? PaginationEnum.PageIndexIntial : pageIndex + PaginationEnum.PageIndexIntial;
		setPageIndex(index);
		const formFiltersOnLoadSearchGrid = formFilters;
		if (formFiltersOnLoadSearchGrid) {
			const updatedFormFilters = {
				...formFiltersOnLoadSearchGrid,
				pageIndex: index,
			};
			onLoadInfiniteScroll(updatedFormFilters, refreshGrid);
		}
		const request = INITIAL_FORM_STATE;
		const firstDateResult = initalDateForm(parameterValueDateRange);
		request.startDate = firstDateResult;

		const secondDateResult = initalDateForm();
		request.finalDate = secondDateResult;
		dispatch(getCartCountStatus(request));
	}

	function handleTimeFinished() {
		setGridData({
			isLoading: true,
			totalItems: 0,
			data: [],
		});
		onLoadSearchGrid(true);
	}

	function handleRowBoxGrid(registerGuid: string) {
		navigate(`${RoutesConstants.quotationCartCreateAnswer}?guidQuote=${registerGuid}`);
	}
	function handleOpenClaimDetails(guid: string) {
		setSelectedGuidQuote(guid);
		setOpenModalClaimDetails(true);
	}

	useEffect(() => {
		setHasShowMore(gridData.data.length < gridData.totalItems);
	}, [gridData.data]);

	useEffect(() => {
		setPageIndex(PaginationEnum.PageIndexIntial);
	}, [formFilters]);

	if (gridData.isLoading) return <LoadingComponent />;
	if (gridData.data && gridData.data.length === 0 && formFilters !== null)
		return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data && gridData.data.length === 0 && !gridData.isLoading) return null;

	// CONSTANTS LAYOUT
	const titleGrid = getDefaultMessageResultsFound(gridData.data.length, gridData.totalItems);
	const isUserSupplierLogged = userIsSupplier();

	const showLicensePlate = isShowControlByElementId(Controls.IdLicensePlate);
	const showAddress = !isUserSupplierLogged;
	const showStateAndAmount = isUserSupplierLogged || !showLicensePlate;
	const columns = [
		{ show: true, key: "quoteNr", size: "6%" },
		{ show: true, key: "claimNumber", size: "12%" },
		{ show: true, key: "insurer", size: "13%" },
		{ show: true, key: "bodyshop", size: "12%" },
		{ show: showAddress, key: "address", size: "10%" },
		{ show: true, key: "vehicle", size: "12%" },
		{ show: showLicensePlate, key: "licensePlate", size: "10%" },
		{
			show: showStateAndAmount,
			key: "state",
			size: "7%",
		},
		{
			show: showStateAndAmount,
			key: "amount",
			size: "7%",
		},
		{ show: true, key: "remainingTimeToExpire", size: "11%" },
		{ show: true, key: "status", size: "10%" },
	];

	const visibleColumns = columns.filter((c) => c.show);
	const rowHeaders = visibleColumns.map((c) => getTranslateByKey(`information.${c.key}`));
	const sizeColumns = visibleColumns.map((c) => c.size);

	return (
		<S.Container>
			<BoxGrid
				id={scrollTargetId}
				maxHeight="50vh"
				title={titleGrid}
				rowHeaders={rowHeaders}
				sizeColumns={sizeColumns}
				infiniteScrollConfig={{
					dataLength: gridData.data.length,
					fetchData: () => onLoadSearchGrid(false),
					hasMore: hasShowMore,
					isLoading: isLoadingInfiniteScroll,
					idScrollTarget: scrollTargetId,
				}}
			>
				{selectedGuidQuote && (
					<ClaimDetailsSearchPendingAnswer
						guidQuote={selectedGuidQuote}
						openModalClaimDetails={openModalClaimDetails}
						setOpenModalClaimDetails={setOpenModalClaimDetails}
						guidScreen={getGuidScreenByRoute(RoutesConstants.quotationCartCreateAnswer)}
					/>
				)}
				{gridData.data.map((cart) => (
					<KOGTableRow key={cart.guidQuote} onClick={() => handleRowBoxGrid(cart.guidQuote)}>
						<TableCell>{cart.code}</TableCell>
						<TableCell>
							<S.ColumnClaim>
								<TextEllipsis>{cart.claimNumber}</TextEllipsis>
								{cart.integration && (
									<IconButton
										size="medium"
										color="primary"
										id={`id-pendingAnswerClaimDetails-icon-${cart.guidQuote}`}
										onClick={(e) => {
											e.stopPropagation();
											handleOpenClaimDetails(cart.guidQuote);
										}}
									>
										<Info />
									</IconButton>
								)}
							</S.ColumnClaim>
						</TableCell>
						<TableCell>
							<div style={{ minWidth: 100 }}>
								<TextEllipsis>{cart.insurerCompanyName}</TextEllipsis>
							</div>
						</TableCell>
						<TableCell>
							<div style={{ minWidth: 110 }}>
								<TooltipComponent title={cart.bodyShopCompanyName} placement="top">
									<TextEllipsis>{cart.bodyShopCompanyName}</TextEllipsis>
								</TooltipComponent>
							</div>
						</TableCell>
						{showAddress && <TableCell>{cart.deliveryAddress}</TableCell>}
						<TableCell>
							<div style={{ minWidth: 100 }}>{cart.vehicleModel}</div>
						</TableCell>
						{showLicensePlate && (
							<TableCell>
								<div style={{ minWidth: 125 }}>{cart.vehicleRegistration ?? "--"}</div>
							</TableCell>
						)}
						{showStateAndAmount && (
							<>
								<TableCell>
									<div style={{ minWidth: 100 }}>{cart.state ?? "--"}</div>
								</TableCell>
								<TableCell>
									<div>{cart.amount ?? "--"}</div>
								</TableCell>
							</>
						)}
						<TableCell>
							<div style={{ minWidth: 100 }}>
								{cart.answerStatus === StatusCartEnum.Pending && (
									<PendingDeadlineCell deadlineAnswer={cart.deadlineAnswer} onTimeFinished={handleTimeFinished} />
								)}
							</div>
						</TableCell>
						<TableCell>
							<S.AreaStatus>
								<StatusCartComponent status={cart.answerStatus} />
							</S.AreaStatus>
						</TableCell>
					</KOGTableRow>
				))}
			</BoxGrid>
		</S.Container>
	);
}

export default PendingSearchGrid;
